import { useState, useMemo, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { getGuideCategoryToBoats, getGuideBoatsListing } from "store/boats"

const useEquipmentManagement = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [assignedEquipment, setAssignedEquipment] = useState([])
  const [showAddEquipment, setShowAddEquipment] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedPools, setSelectedPools] = useState([])

  // Fetch categories and equipment from Redux
  const categoryListing = useSelector((state) => state.boats.category_to_boats.items)
  const equipmentListing = useSelector((state) => state.boats.items)

  useEffect(() => {
    dispatch(getGuideCategoryToBoats())
    dispatch(getGuideBoatsListing())
  }, [dispatch])

  // Transform category data to options format
  const categoryOptions = useMemo(() => {
    const options = categoryListing.map((cat) => ({
      label: cat?.name,
      value: String(cat?.id)
    }))
    return [{ label: t("All") || "All", value: "all" }, ...options]
  }, [categoryListing, t])

  // Transform equipment data to table format
  const availableEquipment = useMemo(() => {
    return equipmentListing.map((equipment) => ({
      id: equipment.id,
      name: equipment.equipment_title || equipment.name || "",
      assetNumber: equipment.asset_number || "-",
      category: typeof equipment.category === "string" ? equipment.category : equipment.category?.name || ""
    }))
  }, [equipmentListing])

  // Transform equipment data to options format
  const equipmentPoolOptions = useMemo(() => {
    const options = equipmentListing.map((equipment) => ({
      label: equipment?.equipment_title || equipment?.name,
      value: String(equipment?.id)
    }))
    return [{ label: "Select All", value: "all" }, ...options]
  }, [equipmentListing])

  // Create a map of category ID to name for filtering
  const categoryIdToNameMap = useMemo(() => {
    return categoryListing.reduce((acc, cat) => {
      if (cat?.id !== undefined && cat?.name) {
        acc[String(cat.id)] = cat.name
      }
      return acc
    }, {})
  }, [categoryListing])

  const handleUnassign = (equipmentId) => {
    setAssignedEquipment((prev) => prev.filter((eq) => eq.id !== equipmentId))
  }

  const handleAssign = (equipment) => {
    setAssignedEquipment((prev) => {
      // Check if already assigned
      if (prev.some((eq) => eq.id === equipment.id)) return prev
      return [...prev, equipment]
    })
  }

  const handleCategoryFilter = (values) => {
    setSelectedCategories(values)
  }

  const handlePoolFilter = (values) => {
    setSelectedPools(values)
  }

  const toggleAddEquipment = () => {
    setShowAddEquipment(!showAddEquipment)
  }

  const closeAddEquipment = () => {
    setShowAddEquipment(false)
  }

  const filteredAvailableEquipment = useMemo(() => {
    // If no equipment selected in pools dropdown, show nothing
    if (selectedPools.length === 0) {
      return []
    }

    // Start with equipment from API, excluding already assigned ones
    const assignedIds = assignedEquipment.map((eq) => eq.id)
    let filtered = availableEquipment.filter((eq) => !assignedIds.includes(eq.id))

    // Filter by selected pools (equipment IDs) - only show selected equipment
    if (!selectedPools.includes("all")) {
      filtered = filtered.filter((eq) => selectedPools.includes(String(eq.id)))
    }

    // Filter by selected categories
    if (selectedCategories.length > 0 && !selectedCategories.includes("all")) {
      const selectedCategoryNames = selectedCategories
        .filter((id) => id !== "all")
        .map((id) => categoryIdToNameMap[id])
        .filter(Boolean)

      filtered = filtered.filter((eq) => selectedCategoryNames.includes(eq.category))
    }

    return filtered
  }, [availableEquipment, assignedEquipment, selectedCategories, selectedPools, categoryIdToNameMap])

  return {
    assignedEquipment,
    availableEquipment,
    filteredAvailableEquipment,
    showAddEquipment,
    selectedCategories,
    selectedPools,
    categoryOptions,
    equipmentPoolOptions,
    handleUnassign,
    handleAssign,
    handleCategoryFilter,
    handlePoolFilter,
    toggleAddEquipment,
    closeAddEquipment,
    setSelectedCategories
  }
}

export default useEquipmentManagement
